import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonCard, IonCardContent, IonItem, IonLabel, IonContent, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const alphabets = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20"];

const BeforeAfterBetweenNumbers: React.FC = () => {
    const [question, setQuestion] = useState("");
    const [correctAnswer, setCorrectAnswer] = useState(" ");
    const [options, setOptions] = useState<string[]>([]);
    const [message, setMessage] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [showImage, setShowImage] = useState(false);

    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Nursery Maths: Before, After & Between Numbers Activity – Mama’s Guidance"; // Set page title
    }, []);

    const generateQuestion = () => {
        setOptions([])
        const randomIndex = Math.floor(Math.random() * (alphabets.length - 2)) + 1;
        const type = Math.floor(Math.random() * 3); // 0 = Before, 1 = After, 2 = Between
        let newQuestion = "";
        let correct = "";

        if (type === 0) {
            newQuestion = `What number comes before '${alphabets[randomIndex]}'?`;
            correct = alphabets[randomIndex - 1];
        } else if (type === 1) {
            newQuestion = `What number comes after '${alphabets[randomIndex]}'?`;
            correct = alphabets[randomIndex + 1];
        } else {
            newQuestion = `Which number is between '${alphabets[randomIndex - 1]}' and '${alphabets[randomIndex + 1]}'?`;
            correct = alphabets[randomIndex];
        }

        setQuestion(newQuestion);
        setCorrectAnswer(correct);

        const optionsArray = [
            correct,
            alphabets[randomIndex + 3] ?? alphabets[randomIndex - 3],
            alphabets[randomIndex + 2] ?? alphabets[randomIndex - 4],
            alphabets[randomIndex - 2],
        ];
        setOptions(shuffleArray(optionsArray));
    };

    const shuffleArray = (array: string[]) => {
        return array.sort(() => Math.random() - 0.5);
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion();
                setShowImage(false);
                setMessage('')
            }, 5000); // Delay reduced to 3 seconds
        } else {
            setMessage("❌ Try Again!");
        }
    };


    return (
        <>
            <Helmet>
                <meta name="description" content='Help nursery kids understand number order with the "Before, After & Between Number" activity on Mamas Guidance — an interactive way to teach children number sequences, counting skills, and early math concepts.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart additionWord matchImage letterOption match-container" style={{ textAlign: "center", padding: "20px" }}>
                        <div className="showInfo" onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Before / After / Between Numbers</h1>
                                <p>
                                    Learning numbers is fun! In this activity, children will understand what comes <strong>before</strong>, what comes <strong>after</strong>, and what number lies <strong>between</strong> two numbers. These concepts are essential for building early math skills in nursery-level students.
                                </p>

                                <h2>📚 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Understanding number order from 1 to 20</li>
                                    <li>Identifying the number that comes before or after a given number</li>
                                    <li>Recognizing the number that comes between two numbers</li>
                                </ul>

                                <h2>🔢 What Comes Before?</h2>
                                <p>
                                    Let’s help children learn what number comes <strong>just before</strong> a given number.
                                </p>
                                <p><em>Example:</em> What comes before 6? → <strong>5</strong></p>

                                <h2>🔢 What Comes After?</h2>
                                <p>
                                    Now we find what number comes <strong>just after</strong> a given number.
                                </p>
                                <p><em>Example:</em> What comes after 3? → <strong>4</strong></p>

                                <h2>🔢 What Comes Between?</h2>
                                <p>
                                    Finding the number that is <strong>in between</strong> two numbers.
                                </p>
                                <p><em>Example:</em> What comes between 7 and 9? → <strong>8</strong></p>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/mathematics/count-object"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Count Object</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/mathematics/missing-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Missing Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/mathematics/bigger-or-smaller"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Bigger Or Smaller</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Worksheet Works</h2>
                                <p>
                                    Visual learning is powerful for toddlers. By connecting pictures with numbers, children build a solid foundation in early numeracy. Worksheets like these enhance <strong>cognitive development</strong> and <strong>encourage independent thinking</strong>.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Before / After / Between Numbers</h1>
                            <p>
                                Learning numbers is fun! In this activity, children will understand what comes <strong>before</strong>, what comes <strong>after</strong>, and what number lies <strong>between</strong> two numbers. These concepts are essential for building early math skills in nursery-level students.
                            </p>

                            <h2>📚 What Will Your Child Learn?</h2>
                            <ul>
                                <li>Understanding number order from 1 to 20</li>
                                <li>Identifying the number that comes before or after a given number</li>
                                <li>Recognizing the number that comes between two numbers</li>
                            </ul>

                            <h2>🔢 What Comes Before?</h2>
                            <p>
                                Let’s help children learn what number comes <strong>just before</strong> a given number.
                            </p>
                            <p><em>Example:</em> What comes before 6? → <strong>5</strong></p>

                            <h2>🔢 What Comes After?</h2>
                            <p>
                                Now we find what number comes <strong>just after</strong> a given number.
                            </p>
                            <p><em>Example:</em> What comes after 3? → <strong>4</strong></p>

                            <h2>🔢 What Comes Between?</h2>
                            <p>
                                Finding the number that is <strong>in between</strong> two numbers.
                            </p>
                            <p><em>Example:</em> What comes between 7 and 9? → <strong>8</strong></p>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/mathematics/count-object"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Count Object</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/missing-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Missing Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/bigger-or-smaller"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Bigger Or Smaller</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Worksheet Works</h2>
                            <p>
                                Visual learning is powerful for toddlers. By connecting pictures with numbers, children build a solid foundation in early numeracy. Worksheets like these enhance <strong>cognitive development</strong> and <strong>encourage independent thinking</strong>.
                            </p>

                        </article>
                        <h2>{question}</h2>
                        <div className="mt-4 mb-5 grid-container object-letter">
                            {options.map((option, index) => (
                                <div className="optionButton background-blue" key={option} onClick={() => checkAnswer(option)}>
                                    {option}
                                </div>
                            ))}
                        </div>
                        <p>{message}</p>
                        <div className="btn hero-btn" onClick={generateQuestion}>Next &gt;&gt;</div>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default BeforeAfterBetweenNumbers;